import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MoreVertical, Paperclip, Smile, BarChart3, Send } from 'lucide-react';
import { UI } from '../constants/designTokens';
import { apiService } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import { ErrorHandler } from '../utils/errorHandler';
import { format } from 'date-fns';
import { useWebSocket } from '../hooks/useWebSocket';

const ConversationPage = () => {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  // WebSocket connection
  const { sendMessage: wsSendMessage, lastMessage } = useWebSocket(conversationId);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversation = useCallback(async () => {
    try {
      const response = await apiService.get(`/chat/conversations/${conversationId}`);
      setConversation(response.data);
    } catch (error) {
      ErrorHandler.logError('ConversationPage - fetchConversation', error);
      if (error.response?.status === 404) {
        navigate('/messages');
      }
    }
  }, [conversationId, navigate]);

  const fetchMessages = useCallback(async (before = null) => {
    try {
      const params = before ? `?before=${before}` : '';
      const response = await apiService.get(`/chat/conversations/${conversationId}/messages${params}`);
      const newMessages = response.data || [];
      
      if (before) {
        // Prepend older messages
        setMessages(prev => [...newMessages, ...prev]);
      } else {
        // Initial load
        setMessages(newMessages);
        setTimeout(scrollToBottom, 100);
      }
    } catch (error) {
      ErrorHandler.logError('ConversationPage - fetchMessages', error);
    } finally {
      setLoading(false);
    }
  }, [conversationId]);

  const markAsRead = useCallback(async () => {
    try {
      await apiService.patch(`/chat/conversations/${conversationId}/read`);
    } catch (error) {
      ErrorHandler.logError('ConversationPage - markAsRead', error);
    }
  }, [conversationId]);

  useEffect(() => {
    if (conversationId && user) {
      setLoading(true);
      fetchConversation();
      fetchMessages();
      markAsRead();
    }
  }, [conversationId, user, fetchConversation, fetchMessages, markAsRead]);

  // Handle WebSocket messages
  useEffect(() => {
    if (lastMessage) {
      const data = JSON.parse(lastMessage.data);
      if (data.type === 'new_message' && data.conversation_id === conversationId) {
        const newMsg = data.message;
        setMessages(prev => {
          // Avoid duplicates
          if (prev.some(m => m.id === newMsg.id)) {
            return prev;
          }
          return [...prev, newMsg];
        });
        setTimeout(scrollToBottom, 100);
        markAsRead();
      }
    }
  }, [lastMessage, conversationId, markAsRead]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messages.length > 0 && !loading) {
      scrollToBottom();
    }
  }, [messages.length, loading]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageInput.trim() || sending) return;

    const content = messageInput.trim();
    setMessageInput('');
    setSending(true);

    try {
      // Try WebSocket first
      if (wsSendMessage) {
        wsSendMessage({
          type: 'message',
          conversation_id: conversationId,
          content: content,
        });
      } else {
        // Fallback to REST API
        const response = await apiService.post(
          `/chat/conversations/${conversationId}/messages`,
          { content }
        );
        setMessages(prev => [...prev, response.data]);
        setTimeout(scrollToBottom, 100);
      }
    } catch (error) {
      ErrorHandler.logError('ConversationPage - sendMessage', error);
      setMessageInput(content); // Restore message on error
    } finally {
      setSending(false);
    }
  };

  const formatMessageTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const isToday = format(date, 'yyyy-MM-dd') === format(now, 'yyyy-MM-dd');
    
    if (isToday) {
      return format(date, 'h:mm a');
    } else {
      return format(date, 'MMM d, h:mm a');
    }
  };

  const formatDateHeader = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const isToday = format(date, 'yyyy-MM-dd') === format(now, 'yyyy-MM-dd');
    
    if (isToday) {
      return 'Today';
    } else {
      return format(date, 'MMMM d, yyyy');
    }
  };

  // Group messages by date
  const groupedMessages = messages.reduce((groups, message) => {
    const date = format(new Date(message.created_at), 'yyyy-MM-dd');
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(message);
    return groups;
  }, {});

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: UI.COLORS.BACKGROUND }}>
        <div style={{ color: UI.COLORS.TEXT_PRIMARY }}>Please log in to view conversations</div>
      </div>
    );
  }

  if (loading && !conversation) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: UI.COLORS.BACKGROUND }}>
        <div style={{ color: UI.COLORS.TEXT_SECONDARY }}>Loading conversation...</div>
      </div>
    );
  }

  if (!conversation) {
    return null;
  }

  const otherParticipant = {
    name: conversation.other_participant_name || 'Unknown User',
    profilePicture: conversation.other_participant_profile_picture,
    type: conversation.other_participant_type,
  };

  return (
    <div className="flex flex-col h-screen" style={{ backgroundColor: UI.COLORS.BACKGROUND, color: UI.COLORS.TEXT_PRIMARY }}>
      {/* Header */}
      <div className="sticky top-0 z-10" style={{ backgroundColor: UI.COLORS.BACKGROUND, borderBottom: `1px solid ${UI.COLORS.BORDER}` }}>
        <div className="flex items-center justify-between px-4 py-4">
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <button
              onClick={() => navigate('/messages')}
              className="p-2 hover:opacity-70 transition-opacity flex-shrink-0"
              style={{ color: UI.COLORS.TEXT_PRIMARY }}
            >
              <ArrowLeft size={24} />
            </button>
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {/* Avatar */}
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                style={{
                  backgroundColor: otherParticipant.profilePicture ? 'transparent' : UI.COLORS.ACCENT,
                }}
              >
                {otherParticipant.profilePicture ? (
                  <img
                    src={otherParticipant.profilePicture}
                    alt={otherParticipant.name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <span style={{ color: UI.COLORS.WHITE, fontFamily: UI.FONT_FAMILY.BOLD, fontSize: UI.FONT_SIZES.SM }}>
                    {otherParticipant.name.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h2
                  className="font-semibold truncate"
                  style={{
                    fontFamily: UI.FONT_FAMILY.BOLD,
                    fontSize: UI.FONT_SIZES.LG,
                  }}
                >
                  {otherParticipant.name}
                </h2>
                <p className="text-sm truncate" style={{ color: UI.COLORS.TEXT_TERTIARY }}>
                  2 members
                </p>
              </div>
            </div>
          </div>
          <button
            className="p-2 hover:opacity-70 transition-opacity flex-shrink-0"
            style={{ color: UI.COLORS.TEXT_PRIMARY }}
          >
            <MoreVertical size={24} />
          </button>
        </div>
      </div>

      {/* Messages Area */}
      <div
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto px-4 py-4"
        style={{ backgroundColor: UI.COLORS.BACKGROUND }}
      >
        {loading && messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div style={{ color: UI.COLORS.TEXT_SECONDARY }}>Loading messages...</div>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full">
            <div className="text-center mb-4" style={{ color: UI.COLORS.TEXT_TERTIARY }}>
              {format(new Date(conversation.created_at), 'MMMM d, yyyy')}
            </div>
            <div className="text-center" style={{ color: UI.COLORS.TEXT_SECONDARY }}>
              {user.name} created this conversation.
            </div>
          </div>
        ) : (
          Object.entries(groupedMessages).map(([date, dateMessages]) => (
            <div key={date}>
              {/* Date Header */}
              <div className="text-center my-4" style={{ color: UI.COLORS.TEXT_TERTIARY, fontSize: UI.FONT_SIZES.SM }}>
                {formatDateHeader(dateMessages[0].created_at)}
              </div>
              
              {/* Messages for this date */}
              {dateMessages.map((message) => {
                const isOwnMessage = message.sender_id === user.id;
                return (
                  <div
                    key={message.id}
                    className={`mb-4 flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[75%] rounded-lg px-4 py-2 ${
                        isOwnMessage ? 'rounded-br-sm' : 'rounded-bl-sm'
                      }`}
                      style={{
                        backgroundColor: isOwnMessage ? UI.COLORS.ACCENT : UI.COLORS.FOREGROUND,
                        color: isOwnMessage ? UI.COLORS.WHITE : UI.COLORS.TEXT_PRIMARY,
                      }}
                    >
                      {!isOwnMessage && (
                        <div className="text-xs mb-1" style={{ color: UI.COLORS.TEXT_TERTIARY, opacity: 0.8 }}>
                          {message.sender_name}
                        </div>
                      )}
                      <p
                        className="break-words"
                        style={{
                          fontFamily: UI.FONT_FAMILY.PRIMARY,
                          fontSize: UI.FONT_SIZES.MD,
                        }}
                      >
                        {message.content}
                      </p>
                      <div
                        className="text-xs mt-1 flex items-center gap-2"
                        style={{ opacity: 0.7 }}
                      >
                        <span>{formatMessageTime(message.created_at)}</span>
                        {isOwnMessage && message.is_read && (
                          <span>✓✓</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Bar */}
      <div
        className="sticky bottom-0 px-4 py-3"
        style={{
          backgroundColor: UI.COLORS.BACKGROUND,
          borderTop: `1px solid ${UI.COLORS.BORDER}`,
        }}
      >
        <form onSubmit={handleSendMessage} className="flex items-center gap-2">
          <button
            type="button"
            className="p-2 hover:opacity-70 transition-opacity"
            style={{ color: UI.COLORS.TEXT_SECONDARY }}
          >
            <Paperclip size={20} />
          </button>
          <button
            type="button"
            className="p-2 hover:opacity-70 transition-opacity"
            style={{ color: UI.COLORS.TEXT_SECONDARY }}
          >
            <Smile size={20} />
          </button>
          <button
            type="button"
            className="p-2 hover:opacity-70 transition-opacity"
            style={{ color: UI.COLORS.TEXT_SECONDARY }}
          >
            <BarChart3 size={20} />
          </button>
          <input
            type="text"
            value={messageInput}
            onChange={(e) => setMessageInput(e.target.value)}
            placeholder="Send a message"
            className="flex-1 px-4 py-2 rounded-lg"
            style={{
              backgroundColor: UI.COLORS.FOREGROUND,
              color: UI.COLORS.TEXT_PRIMARY,
              border: `1px solid ${UI.COLORS.BORDER}`,
              fontFamily: UI.FONT_FAMILY.PRIMARY,
            }}
            disabled={sending}
          />
          <button
            type="submit"
            disabled={!messageInput.trim() || sending}
            className="p-2 hover:opacity-70 transition-opacity disabled:opacity-50"
            style={{ color: UI.COLORS.ACCENT }}
          >
            <Send size={20} />
          </button>
        </form>
      </div>
    </div>
  );
};

export default ConversationPage;

